/**
 * SendingDomainNotice.tsx — per-user note about the domain emails leave from.
 *
 * Emails are sent from the mailbox each user connected in MailBridge, so the
 * warm-up cap and DNS checks that apply are those of THAT mailbox's domain.
 * This component shows the logged-in user's situation wherever email is
 * sent (MailBridge page, Scheduler, Campaign pre-flight, Sequences
 * "Send Now", Reply Inbox):
 *
 *   • mailbox domain not registered under Setup → Domains → amber note
 *     (sending is NOT blocked; no warm-up limits / DNS checks are applied)
 *   • subdomain whose parent is registered            → amber note
 *   • no mailbox connected                            → amber note
 *   • MailBridge could not be reached                 → muted note
 *   • registered domain                               → nothing, or the
 *     optional one-line summary (week, today's usage) with `showSummary`
 *
 * Data: GET /api/v1/mailbridge/sending-context (cached 60 s, shared by every
 * instance on the page through the same React Query key).
 */
import { AlertTriangle, Globe, Info, Mail } from "lucide-react";
import { Link } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { NOTICE_TITLE, sendingSummaryText } from "./sendingSummary";
import { useSendingContext } from "./useSendingContext";

interface SendingDomainNoticeProps {
  /** Also show a one-line summary when there is no notice (registered domain). */
  showSummary?: boolean;
  /** Smaller, single-paragraph rendering for dialogs and cards. */
  compact?: boolean;
  className?: string;
}

export function SendingDomainNotice({
  showSummary = false,
  compact = false,
  className,
}: SendingDomainNoticeProps) {
  const { data: ctx } = useSendingContext();
  const { hasRole } = useAuth();
  if (!ctx) return null;

  const canManageDomains = hasRole("MANAGER");

  if (!ctx.notice) {
    if (!showSummary || ctx.mailbox_state !== "connected") return null;
    const summary = sendingSummaryText(ctx);
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 text-xs text-muted-foreground",
          className,
        )}
      >
        <Mail className="h-3.5 w-3.5" />
        <span>
          Sending From <span className="font-medium text-foreground">{ctx.mailbox_email}</span>
        </span>
        {summary && (
          <Badge variant="secondary" className="text-[10px]">
            {summary}
          </Badge>
        )}
        {ctx.dns_failing.length > 0 && (
          <Badge variant="destructive" className="text-[10px]">
            DNS Failing: {ctx.dns_failing.join(", ")}
          </Badge>
        )}
      </div>
    );
  }

  const isWarning = ctx.notice_code !== "mailbox_unknown";
  const Icon = isWarning ? AlertTriangle : Info;
  const title = NOTICE_TITLE[ctx.notice_code ?? ""] ?? "Sending Domain Note";
  const unregistered =
    ctx.notice_code === "domain_not_registered" ||
    ctx.notice_code === "subdomain_not_registered";

  const action = unregistered ? (
    canManageDomains ? (
      <Link to="/setup/domains" className="font-medium underline underline-offset-2">
        <Globe className="mr-1 inline h-3.5 w-3.5" />
        Add {ctx.mailbox_domain} in Setup → Domains
      </Link>
    ) : (
      <span>Ask an admin to add {ctx.mailbox_domain} under Setup → Domains.</span>
    )
  ) : ctx.notice_code === "no_mailbox" ? (
    <Link to="/setup/mailbridge" className="font-medium underline underline-offset-2">
      <Mail className="mr-1 inline h-3.5 w-3.5" />
      Connect a mailbox
    </Link>
  ) : null;

  if (compact) {
    return (
      <div
        role="note"
        className={cn(
          "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
          isWarning
            ? "border-amber-500/50 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
            : "border-border bg-muted/40 text-muted-foreground",
          className,
        )}
      >
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="space-y-1">
          <p>{ctx.notice}</p>
          {action && <p>{action}</p>}
        </div>
      </div>
    );
  }

  return (
    <Alert variant={isWarning ? "warning" : "default"} className={className}>
      <Icon className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-1">
        <p>{ctx.notice}</p>
        {action && <p>{action}</p>}
      </AlertDescription>
    </Alert>
  );
}

/** Inline note for a notice string returned by a send/tick response. */
export function SendingNoticeText({
  notice,
  className,
}: {
  notice: string | null | undefined;
  className?: string;
}) {
  if (!notice) return null;
  return (
    <div
      role="note"
      className={cn(
        "flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <p>{notice}</p>
    </div>
  );
}
